<template>
  <v-card>
    <v-skeleton-loader :loading="loadingData" type="article">
      <h4 class="px-8 py-4">
        Dispositivos
      </h4>
      <v-divider></v-divider>
      <div v-if="devices.length > 0">
        <div v-for="(device, index) in devices" :key="index">
          <v-card-title>
            <v-row align="center" justify="space-between">
              <v-col cols="1">
                <v-btn class="ma-2" outlined large fab color="grey">
                  <v-icon color="grey">{{ getDeviceIcon(device.os) }}</v-icon>
                </v-btn>
              </v-col>
              <v-col>
                <v-row class="mt-1 mb-0 pb-0">
                  <v-col class="my-0 py-0">
                    <span class="font-weight-regular text-md-h6">
                      {{ device.os }}
                    </span>
                    <span
                      class="font-weight-regular text-md-subtitle-2 ml-2 text--disabled"
                    >
                      {{ device.linkedSince }}
                    </span>
                  </v-col>
                </v-row>
                <v-row class="my-0 py-0">
                  <v-col class="my-0 py-0">
                    <span class="font-weight-regular text-md-h6 text--disabled">
                      {{ device.name }}
                    </span>
                  </v-col>
                </v-row>
                <v-row class="my-1" v-if="device.isThisDevice">
                  <v-col class="my-0 py-0">
                    <span
                      class="font-weight-bold green--text text-md-subtitle-2"
                    >
                      {{
                        $t(
                          'portal.general.user_settings.user_profile.labels.card_admin_is_this_devices'
                        )
                      }}
                    </span>
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-card-actions>
              <v-btn
                :id="`idBtnPortalDesvincular${index}`"
                v-if="!device.isThisDevice"
                text
                class="tertiary--text text-capitalize"
                @click="setDeviceToDelete(index)"
              >
                {{
                  $t(
                    'portal.general.user_settings.user_profile.labels.card_admin_devices_unlink'
                  )
                }}
              </v-btn>
            </v-card-actions>
          </v-card-title>
          <v-divider></v-divider>
        </div>
      </div>
      <div v-else align="center" class="px-8 py-4">
        <h2 class="font-weight-bold">
          {{
            $t(
              'portal.general.user_settings.user_profile.labels.card_admin_devices_not_have_linked_devices'
            )
          }}
        </h2>
      </div>
      <div class="px-8 py-4" v-if="devices.length !== 0">
        <v-btn
          id="idBtnPortalDesvincularTodos"
          text
          class="tertiary--text text-capitalize"
          @click="prepareToDeleteAllDevices"
        >
          {{
            $t(
              'portal.general.user_settings.user_profile.labels.card_admin_devices_unpair_all_devices'
            )
          }}
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </v-skeleton-loader>
    <Dialog
      v-if="showDialog"
      :showDialog="showDialog"
      :loading="dialog.loading"
      :text="dialog.text"
      type="warning"
      @cancelButtonPressed="cancel()"
      @submitButtonPressed="dialog.acceptFunction()"
    />
  </v-card>
</template>

<script>
import { DeviceHelpers } from '@/helpers/mainExports'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/es'
import { DEVICE_IDENTIFIER } from '@/constants/devices'
import Dialog from '../dialog/Dialog'
import i18n from '../../plugins/i18n'

export default {
  components: {
    Dialog
  },
  data() {
    return {
      loadingData: true,
      devices: [],
      showDialog: false,
      currentDeviceIndex: null,
      currentDeviceId: '',
      dialog: {
        text: '',
        type: '',
        loading: false,
        acceptFunction: () => null
      }
    }
  },
  methods: {
    getDeviceIcon(os) {
      if (os === 'Unknow') return 'mdi-devices'
      if (['MacIntel', 'Win32', 'Linux x86_64', 'Linux armv81'].includes(os))
        return 'mdi-monitor'
      return 'mdi-cellphone'
    },
    setDeviceToDelete(index) {
      this.currentDeviceIndex = index
      this.currentDeviceId = this.devices[index].id
      this.dialog.acceptFunction = this.deleteDevice
      this.dialog.text = i18n.t('portal.general.user_settings.user_profile.alerts.card_admin_confirm_unpair_device')
      this.showDialog = true
    },
    async deleteDevice() {
      if (this.currentDeviceIndex !== null && this.currentDeviceId !== '') {
        try {
          this.dialog.loading = true
          await DeviceHelpers.deleteDevice(this.currentDeviceId)
        } catch (e) {
          console.log(e)
        }
        finally {
          await this.getDevices()
          this.currentDeviceIndex = null
          this.currentDeviceId = ''
          this.dialog.loading = false
          this.showDialog = false
        }
      }
    },
    prepareToDeleteAllDevices() {
      this.dialog.text = i18n.t('portal.general.user_settings.user_profile.alerts.card_admin_confirm_unpair_all_devices')
      this.dialog.acceptFunction = this.deleteAllDevices
      this.showDialog = true
    },
    async deleteAllDevices() {
      let promiseArray = []
      try {
        this.dialog.loading = true
        this.devices.forEach(async device => {
          if (!device.isThisDevice) {
            promiseArray.push(DeviceHelpers.deleteDevice(device.id))
          }
        })
        await Promise.allSettled(promiseArray)
      } catch (e) {
        console.error(e)
      }
      finally {
        await this.getDevices()
        this.dialog.loading = false
        this.showDialog = false
      }
    },
    cancel() {
      this.currentDeviceIndex = null
      this.currentDeviceId = ''
      this.showDialog = false
    },
    formatSiceTimestamp(date) {
      dayjs.extend(relativeTime)
      dayjs.locale('es')
      const now = dayjs()
      const dateDayjs = dayjs(date)
      const daysElapsed = now.diff(dateDayjs, 'day')
      const hoursElapsed = now.diff(dateDayjs, 'hour')
      const minutesElapsed = now.diff(dateDayjs, 'minute')

      if (daysElapsed > 7) {
        return dateDayjs.format('DD/MM/YYYY HH:mm')
      } else if (daysElapsed > 0) {
        return dateDayjs.fromNow()
      } else if (hoursElapsed > 0) {
        const hours = Math.floor(hoursElapsed)
        const minutes = minutesElapsed % 60
        if (minutes > 0) {
          return `hace ${hours} ${
            hours === 1 ? 'hora' : 'horas'
          } y ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`
        } else {
          return `hace ${hours} ${hours === 1 ? 'hora' : 'horas'}`
        }
      } else {
        return `hace ${minutesElapsed} ${
          minutesElapsed === 1 ? 'minuto' : 'minutos'
        }`
      }
    },
    async getDevices() {
      try {
        const data = await DeviceHelpers.find()
        this.devices = data.map(device => {
          return {
            id: device.id,
            name: device.info.name,
            os: device.info.system_name,
            linkedSince: this.formatSiceTimestamp(device.created_timestamp),
            isThisDevice:
              device.info.identifier === localStorage.getItem(DEVICE_IDENTIFIER)
          }
        })
      } catch (e) {
        console.error(e)
      }
    }
  },
  computed: {
    sessionIsOpen() {
      return this.$store.getters['auth/connection']?.session?.isOpen
    }
  },
  async created() {
    await this.getDevices()
    this.loadingData = false
  },
  watch: {
    async sessionIsOpen(newValue) {
      if (newValue) {
        await this.getDevices()
        this.loadingData = false
      }
    }
  }
}
</script>
